import { ClientService, ErrorHandlerService, ToastMessageService } from '@fitness-force/api';
import { CommonModule } from '@angular/common';

import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnDestroy
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import {
  AppErrors,
  BadRequestError,
  ErrorMsgComponent,
  NotFoundError,
} from '@fitness-force/errors';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';

import { BehaviorSubject, Subscription, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { TENANTSINTERFACE, UpdateClientRequest } from '@fitness-force/models';
import { Store } from '@ngrx/store';
// Note: Import issue with relative path - will be handled in actual implementation
import { HTTP_RES_ERROR_CODE } from '@fitness-force/enum';

@Component({
  selector: 'assign-key-fob',
  templateUrl: './assign-key-fob.component.html',
  styleUrls: ['./assign-key-fob.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [        CommonModule,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    ErrorMsgComponent,
    TranslateModule
  ]
})
export class AssignKeyFobComponent implements OnInit, OnDestroy {
  @Input() assignKeyFobModal: boolean | null = false;
  @Input() clientData$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  @Input() clientId: number = 0;
  @Output() closeassignKeyFobModalDialog = new EventEmitter<any>();
  assignKeyFobFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  //validAlphaNumericPattern = "^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$";
  validAlphaNumericPattern = "^[a-zA-Z0-9]*$";
  allClientData: any = null;
  tenantId: number = 0;
  btnLoading: boolean = false;
  isValidatingKeyfob: boolean = false;
  isKeyfobValid: boolean = false;
  keyfobValidationMessage: string = '';
  private errorHandlerSubscription: Subscription;
  private keyfobValidationSubscription: Subscription;

  
  constructor(
    private fb: UntypedFormBuilder,
    public translate: TranslateService,
    private clientService: ClientService,
    private toastMessageService: ToastMessageService,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private errorHandlerService: ErrorHandlerService,
    private toastService:ToastMessageService
  ) {
    this.errorHandlerSubscription = this.errorHandlerService.error$.subscribe(
      (error:AppErrors) => {
        if(error.originalError.status === HTTP_RES_ERROR_CODE.FORBIDDEN)
        {
          this.btnLoading=false;
          this.handleError(error);
        }
      }
    );

    // Keyfob validation will be set up in componentInit() after tenantId is available
  }

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    // Get tenant info from store state
    this.store.select((state: any) => state.tenants).subscribe((tenant: TENANTSINTERFACE) => {
      if (tenant && tenant.selected_tenant) {
        this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.clientData$.subscribe((res: any) => {
          this.allClientData = res;
          this.assignKeyFobFormGroup = this.fb.group({
            keyFobNumber: [
              res?.current_keyfob ? res?.current_keyfob : '',
              [
                Validators.required,
                Validators.maxLength(12),
                Validators.pattern(this.validAlphaNumericPattern)
              ]
            ],
          });

          // Set up form value changes listener with direct API call and debounce
          this.keyfobValidationSubscription = this.keyFobNumber.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            switchMap((value: string) => {
              if (value && value.trim() && value !== this.allClientData?.current_keyfob && this.tenantId > 0) {
                this.isValidatingKeyfob = true;
                this.keyfobValidationMessage = '';
                this.changeDetectorRef.detectChanges();
                                 return this.clientService.validateKeyfob(this.tenantId, value.trim()).pipe(
                   catchError((error: any) => {
                    // Handle error without breaking the stream
                    this.isValidatingKeyfob = false;
                    this.isKeyfobValid = false;
                    if (error?.originalError?.error?.errorMessage) {
                      this.keyfobValidationMessage = error.originalError.error.errorMessage;
                    } else {
                      this.keyfobValidationMessage = 'Invalid keyfob number';
                    }
                    this.changeDetectorRef.detectChanges();
                    return of(null); // Return null to continue the stream
                  })
                );
              } else {
                this.isValidatingKeyfob = false;
                this.isKeyfobValid = false;
                this.keyfobValidationMessage = '';
                this.changeDetectorRef.detectChanges();
                return of(null);
              }
            })
          ).subscribe((response) => {
            if (response !== null) {
              this.isValidatingKeyfob = false;
              if(response.isValid){
                this.isKeyfobValid = true;
                this.keyfobValidationMessage = 'Keyfob is valid';
              }
              else{
                this.isKeyfobValid = false;
                this.keyfobValidationMessage = "Invalid keyfob";
              }
              this.changeDetectorRef.detectChanges();
            }
          });
        });

        if (this.clientId && this.clientId > 0) {
          this.clientService.getClientById(this.clientId).subscribe({
            next: (res: any) => {
              this.clientData$.next(res);
            },
            error: () => {
              // Handle error silently
            },
          });
        }
      }
      }
    });
  }



  defaultAssignKeyFobFormValue() {
    this.keyFobNumber.setValue(this.allClientData.mobile_number);
  }

  get keyFobNumber(): UntypedFormControl {
    return this.assignKeyFobFormGroup.controls
      .keyFobNumber as UntypedFormControl;
  }

  closeDialogEvent() {
    this.closeassignKeyFobModalDialog.emit(false);
  }
  postAssignKeyFob() {
    // Check if form is valid and keyfob is validated (if changed)
    const isFormValid = this.assignKeyFobFormGroup.valid;
    const currentValue = this.keyFobNumber.value;
    const currentKeyfob = this.allClientData?.current_keyfob || '';
    const isKeyfobChanged = currentValue !== currentKeyfob;
    const canSubmit = isFormValid && (!isKeyfobChanged || this.isKeyfobValid);

    if (canSubmit) {
      this.btnLoading = true;
      const body: Array<{ op: string, path: string, value: any }> = [];
      body.push({ op: "replace", path: "/CurrentKeyfob", value: currentValue ? currentValue : this.allClientData.current_keyfob });
      
      if (!this.allClientData || !this.allClientData.id) {
        this.btnLoading = false;
        this.toastService.showErrorMessage('Client data is not available');
        return;
      }
      
      this.clientService.patchClient(this.allClientData.id, this.tenantId, body)
        .subscribe({
          next: async (res) => {
            this.btnLoading = false;
            this.translate
              .get('CLIENT_PROFILE.KYE_FOB.SUCCESS_MSG')
              .subscribe((translation) => {
                this.toastService.showSuccess(translation);
              });
            await this.timeout(1000);
            this.btnLoading = false;
            this.closeassignKeyFobModalDialog.emit({ action: 'updated' });
          },
          error: (error) => {
            this.btnLoading = false;
            this.handleError(error);
          },
        });
    } else {
      Object.keys(this.assignKeyFobFormGroup.controls).forEach((field) => {
        const control = this.assignKeyFobFormGroup.get(field);
        control?.markAsDirty({ onlySelf: true });
      });
    }
  }

  get canSubmitForm(): boolean {
    const isFormValid = this.assignKeyFobFormGroup.valid;
    const currentValue = this.keyFobNumber.value;
    const currentKeyfob = this.allClientData?.current_keyfob || '';
    const isKeyfobChanged = currentValue !== currentKeyfob;
    return isFormValid && (!isKeyfobChanged || this.isKeyfobValid) && !this.isValidatingKeyfob;
  }

  handleError(error: any) {
    if (error?.originalError?.error?.length > 0) {
      error.originalError.error.forEach((e: any) => {
        if (e.errorCode == 'alreadyexist' && e.identifier == 'client_current_keyfob') {
          this.keyFobNumber.setErrors({
            duplicate: true,
          });
          this.toastService.showErrorMessage(e.errorMessage)
          this.changeDetectorRef.detectChanges();
        }
        else{
          this.toastService.showErrorMessage(e.errorMessage);
          this.changeDetectorRef.detectChanges();
        }
      });
    } else if (error?.originalError?.error) {
      if (error.originalError.error.errorCode == 'alreadyexist' && error.originalError.error.identifier == 'client_current_keyfob') {
        this.keyFobNumber.setErrors({
          duplicate: true,
        });
        this.toastService.showErrorMessage(error.originalError.error.errorMessage);
        this.changeDetectorRef.detectChanges();
      }
      else{
        this.toastService.showErrorMessage(error.originalError.error.errorMessage || 'An error occurred')
      }
      this.changeDetectorRef.detectChanges();
    } else {
      this.toastService.showErrorMessage('An error occurred');
      this.changeDetectorRef.detectChanges();
    }
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  ngOnDestroy(): void {
    if (this.errorHandlerSubscription) {
      this.errorHandlerSubscription.unsubscribe();
    }
  }
}